/**
 * Shared utility functions for chopping task analysis and reporting
 */

/**
 * Calculate total weight from task description
 * @param {string} description - Task description containing weight information
 * @returns {number} Total weight in grams
 */
export function calculateTotalWeight(description) {
  if (!description) return 0;

  let totalWeight = 0;
  const lines = description.split('\n');

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine) {
      // Match patterns like "1200g", "1.5kg", "500 g", "2.3 kg", etc.
      const weightMatch = trimmedLine.match(/^(\d+(?:\.\d+)?)\s*(g|kg|grams?|kilograms?)/i);
      if (weightMatch) {
        const value = parseFloat(weightMatch[1]);
        const unit = weightMatch[2].toLowerCase();

        // Convert to grams
        if (unit.startsWith('kg') || unit.startsWith('kilogram')) {
          totalWeight += value * 1000;
        } else {
          totalWeight += value;
        }
      }
    }
  }

  return totalWeight;
}

/**
 * Format weight for display
 * @param {number} grams - Weight in grams
 * @returns {string} Formatted weight string
 */
export function formatWeight(grams) {
  if (grams === 0) return '0g';
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  } else {
    return `${grams.toFixed(0)}g`;
  }
}

/**
 * Calculate processing rate in kg/hr
 * @param {string} description - Task description
 * @param {number} elapsed - Elapsed time in milliseconds
 * @returns {number} Rate in kg/hr
 */
export function calculateRate(description, elapsed) {
  const totalWeightGrams = calculateTotalWeight(description);
  const totalWeightKg = totalWeightGrams / 1000;

  if (!elapsed || elapsed === 0 || totalWeightKg === 0) {
    return 0;
  }

  const durationHours = elapsed / (1000 * 60 * 60); // Convert ms to hours
  return totalWeightKg / durationHours; // kg/hr
}

/**
 * Format rate for display
 * @param {number} rate - Rate in kg/hr
 * @returns {string} Formatted rate string
 */
export function formatRate(rate) {
  if (rate===undefined) return '';
  rate = Number(rate);
  if (rate === 0) return '0.0';
  return rate.toFixed(1);
}

/**
 * Calculate actual processing rate
 * @param {number} totalWeightKg - Total weight in kilograms
 * @param {number} durationHours - Duration in hours
 * @returns {string|null} Formatted rate or null if invalid
 */
export function calculateActualRate(totalWeightKg, durationHours) {
  if (totalWeightKg <= 0 || durationHours <= 0) {
    return null;
  }

  const rateKgPerHour = totalWeightKg / durationHours;
  return rateKgPerHour.toFixed(2);
}

/**
 * Get estimated rate from task data
 * @param {Object} task - Task object with apiRate property
 * @returns {string|null} Formatted rate or null if not available
 */
export function getEstimatedRate(task) {
  if (task.apiRate) {
    const rate = parseFloat(task.apiRate);
    if (rate > 0) {
      return rate.toFixed(2);
    }
  }
  return null;
}

/**
 * Calculate estimated duration using processing rate
 * @param {number} totalWeightKg - Total weight in kilograms
 * @param {number} rateKgPerHour - Processing rate in kg/hr
 * @returns {number|null} Duration in hours or null if invalid
 */
export function calculateEstimatedDuration(totalWeightKg, rateKgPerHour) {
  if (!totalWeightKg || !rateKgPerHour || totalWeightKg <= 0 || rateKgPerHour <= 0) {
    return null;
  }

  return totalWeightKg / rateKgPerHour;
}

/**
 * Calculate performance comparison between actual and estimated duration
 * @param {number} actualHours - Actual duration in hours
 * @param estimatedHours
 * @returns {Object|null} Performance object with text and color, or null
 */
export function calculatePerformance(actualHours, estimatedHours) {
  if (estimatedHours === null) {
    return null;
  }

  const percentageDifference = ((actualHours - estimatedHours) / estimatedHours) * 100;

  if (percentageDifference < 0) {
    // Faster than estimate - calculate how much faster as percentage
    return {
      text: `${-percentageDifference.toFixed()}% faster`,
      color: 'green'
    };
  } else if (percentageDifference > 0) {
    // Slower than estimate - calculate how much slower as percentage  

    return {
      text: `${percentageDifference.toFixed()}% slower`,
      color: 'red'
    };
  } else {
    // Exactly on target
    return {
      text: 'On target',
      color: 'blue'
    };
  }
}

/**
 * Format duration from milliseconds to hours or minutes
 * @param {number} ms - Duration in milliseconds
 * @returns {string} Formatted duration in hours or minutes
 */
export function formatDuration(ms) {
  if (!ms) return '';
  ms = Number(ms);
  if (isNaN(ms)) return '';

  const hours = (ms / 1000) / 3600;
  const mins = hours * 60;
  const secs = mins * 60;

  if (hours >= 1) {
    return `${hours.toFixed(2)} h`;
  }
  if (mins >= 1)
    return `${mins.toFixed(1)} min`;
  if (secs >= 1)
    return `${secs.toFixed(0)} sec`

  return `${ms} ms`
}

/**
 * Create weight distribution bins from task weights
 * @param {Array} weights - Array of weights in kg
 * @returns {Array} Array of bin objects with min, max, count properties
 */
export function createWeightBins(weights) {
  if (weights.length === 0) return [];
  
  const validWeights = weights.filter(weight => weight > 0);
  if (validWeights.length === 0) return [];
  
  // Remove outliers using IQR method
  const sortedWeights = [...validWeights].sort((a, b) => a - b);
  const q1 = sortedWeights[Math.floor(sortedWeights.length * 0.25)];
  const q3 = sortedWeights[Math.floor(sortedWeights.length * 0.75)];
  const iqr = q3 - q1;
  const lowerBound = q1 - 1.5 * iqr;
  const upperBound = q3 + 1.5 * iqr;
  
  const clippedWeights = validWeights.filter(weight => weight >= lowerBound && weight <= upperBound);
  
  if (clippedWeights.length === 0) {
    // Fallback to original data if all weights are outliers
    const minWeight = Math.min(...validWeights);
    const maxWeight = Math.max(...validWeights);
    return [{
      min: minWeight,
      max: maxWeight,
      count: validWeights.length
    }];
  }
  
  const minWeight = Math.min(...clippedWeights);
  const maxWeight = Math.max(...clippedWeights);
  const binCount = Math.min(8, clippedWeights.length); // Max 8 bins, or number of tasks if less
  
  if (minWeight === maxWeight) {
    return [{
      min: minWeight,
      max: maxWeight,
      count: clippedWeights.length
    }];
  }
  
  const binWidth = (maxWeight - minWeight) / binCount;
  const bins = [];
  
  for (let i = 0; i < binCount; i++) {
    const binMin = minWeight + (i * binWidth);
    const binMax = minWeight + ((i + 1) * binWidth);
    
    const count = clippedWeights.filter(weight => {
      return i === binCount - 1 ? 
        (weight >= binMin && weight <= binMax) : 
        (weight >= binMin && weight < binMax);
    }).length;
    
    bins.push({
      min: binMin,
      max: binMax,
      count: count
    });
  }
  
  return bins;
}

/**
 * Get top N weight bins by count
 * @param {Array} tasks - Array of task objects
 * @param {number} topN - Number of top bins to return (default: 4)
 * @returns {Array} Array of top N bins sorted by count descending
 */
export function getTopWeightBins(tasks, topN = 4) {
  const weights = tasks.map(task => calculateTotalWeight(task.description) / 1000); // Convert to kg
  const bins = createWeightBins(weights);
  
  // Sort by count descending and take top N
  return bins
    .filter(bin => bin.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

/**
 * Get representative weight for a bin (midpoint)
 * @param {Object} bin - Bin object with min and max properties
 * @returns {number} Representative weight in kg
 */
export function getBinRepresentativeWeight(bin) {
  return (bin.min + bin.max) / 2;
}

/**
 * Export data to CSV format
 * @param {Array} arrData - Array of objects to export
 * @param {string} filename - Filename for the download
 */
export function csvExport(arrData, filename) {
  if (!arrData || arrData.length === 0) {
    return;
  }

  let csvContent = '';
  const delim = ',';
  csvContent += [
    Object.keys(arrData[0]).map(item => `"${item}"`).join(delim),
    ...arrData.map(item => Object.values(item).map(value => `"${value}"`).join(delim))
  ].join('\n');

  const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}